/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosCard, CosmosRichText, CosmosTextVariantEnum, hasContent, shiftHeadingTag } from "@components/elements";
import { useHeadingLevelOffset } from "@components/context";
import { css, cx } from "@panda/css";

import { OrionCardIcon } from "./OrionCardIcon.molecule";

import type { OrionCardFragment } from "@cosmos/hygraph-schema";

const cardStyles = css({
	width: "100%",
	maxW: "100%",
});

/**
 * Icon beside the text from 375px up, icon stacked above it below that (Figma `- 375`).
 *
 * Both directions live inside a media query — never a base-layer `flexDirection` with a
 * breakpoint override, which Panda puts in a lower cascade layer where the base rule silently
 * wins at every size. See the layer gotcha in libraries/cosmos-components/CLAUDE.md.
 */
const cardContentStyles = css({
	display: "flex",
	width: "100%",
	minW: "0",
	gap: "eds.dimension.16",
	xsDown: {
		flexDirection: "column",
		alignItems: "flex-start",
	},
	xs: {
		flexDirection: "row",
		alignItems: "center",
	},
});

const cardTextStyles = css({
	display: "flex",
	flexDirection: "column",
	gap: "eds.dimension.8",
	flexGrow: "1",
	minW: "0",
});

const wrapStyles = css({
	overflowWrap: "anywhere",
});

export function OrionCard({
	icon,
	iconGradient,
	title,
	desc,
	cardHref,
	anchorLinkId,
}: OrionCardFragment): React.JSX.Element | null {
	const hasTitle = !!title?.html && hasContent(title.html);
	const hasDesc = !!desc && hasContent(desc);

	// Card title heading tag, resolved from the surrounding HeadingLevelContext: h3 by default,
	// shifted to h2 (floored) when the section title is promoted to the page h1 so the outline
	// never skips. Rendered as a wrapper element rather than `as` on CosmosRichText to keep the
	// exact DOM/visual of the design — same approach as GeminiCard.
	const CardTitleTag = (shiftHeadingTag("h3", useHeadingLevelOffset()) ?? "h3") as "h2" | "h3" | "h4" | "h5" | "h6";

	if (!icon && !hasTitle && !hasDesc) return null;

	return (
		<div id={anchorLinkId || undefined} className={cx("orion-card-molecule", cardStyles)}>
			<CosmosCard className={cx("orion-card", cardStyles)} variant="material" borderRadius={24} padding={20}>
				<div className={cx("orion-card-content", cardContentStyles)}>
					<OrionCardIcon icon={icon} gradient={iconGradient} />
					{hasTitle || hasDesc ? (
						<CosmosCard.Link href={cardHref || ""} className={cx("orion-card-text", cardTextStyles)}>
							{hasTitle ? (
								<CardTitleTag>
									<CosmosRichText
										variant={CosmosTextVariantEnum.headingSM}
										color="eds.text.contrast"
										html={title.html}
										className={wrapStyles}
									/>
								</CardTitleTag>
							) : null}
							{hasDesc ? (
								<CosmosRichText
									variant={CosmosTextVariantEnum.paragraphSM}
									color="eds.text.secondary"
									html={desc}
									className={wrapStyles}
								/>
							) : null}
						</CosmosCard.Link>
					) : null}
				</div>
			</CosmosCard>
		</div>
	);
}
